import modelBaseV2 from "../model-base-v2.model";

/**
 * Incentive Scheme Variable Model
 *
 * API wrappers for FFIS incentive scheme configuration (the 22 admin routes of
 * api/incentive). Every one of them is mapped to menu WM021101 "Scheme Variables"
 * and will return 403 until that menu is granted to the caller's role, per country,
 * in UserRoleMenuMap.
 *
 * Country and user are NOT sent from here. The backend reads them from the session,
 * and each stored procedure scopes its reads and writes by country - so a schemeId
 * belonging to another country comes back as "not found" rather than as someone
 * else's rates.
 *
 * @author FFIS
 */
const IncentiveSchemeVariableModel = {

  /* ---- reference data, for the pickers ---------------------------------- */

  /*
   * No productGroupCode, deliberately. The calculator registry is global - a calculator is an
   * engine strategy (SELLOUT_COMMISSION_V1, SKU_BOUNTY_V1, ...) and IncentiveCalculatorMaster has
   * no product-group or country column. This used to pass one; the server accepted and ignored it,
   * which made the picker look filtered when it never was.
   */
  getCalculators: () => {
    return modelBaseV2.get('/api/incentive/calculator');
  },

  getPositions: (productGroupCode) => {
    const queryString = modelBaseV2.toQueryString({ productGroupCode });
    return modelBaseV2.get(`/api/incentive/position?${queryString}`);
  },

  /* ---- schemes ---------------------------------------------------------- */

  searchSchemes: (filters) => {
    const queryString = modelBaseV2.toQueryString(filters);
    return modelBaseV2.get(`/api/incentive/scheme?${queryString}`);
  },

  /**
   * The whole scheme in one call - IncentiveSchemeDetailGet returns eight result
   * sets (scheme, components, parameters, rate matrix, AR ladders, caps, grades,
   * disqualification rules) so every tab can render from one response.
   */
  getSchemeDetail: (schemeId) => {
    return modelBaseV2.get(`/api/incentive/scheme/${schemeId}`);
  },

  createScheme: (body) => {
    return modelBaseV2.post('/api/incentive/scheme', { body });
  },

  updateScheme: (schemeId, body) => {
    return modelBaseV2.post(`/api/incentive/scheme/${schemeId}/update`, { body });
  },

  /**
   * Activating supersedes the previous version - it never mutates history. The
   * stored procedure refuses a scheme whose rate matrix is incomplete, so a
   * failure here is a real validation result, not a bug.
   */
  activateScheme: (schemeId) => {
    return modelBaseV2.post(`/api/incentive/scheme/${schemeId}/activate`);
  },

  /** Clone-to-new-period: the dominant workflow, since each quarter starts from the last. */
  cloneScheme: (schemeId, body) => {
    return modelBaseV2.post(`/api/incentive/scheme/${schemeId}/clone`, { body });
  },

  /* ---- per-tab reads ---------------------------------------------------- */
  /* These are served from IncentiveSchemeDetailGet on the backend and return a
     slice of it. There is no per-tab stored procedure, so prefer getSchemeDetail
     when the page needs more than one tab - which on first load it always does. */

  getComponents: (schemeId) => modelBaseV2.get(`/api/incentive/scheme/${schemeId}/component`),
  getParameters: (schemeId) => modelBaseV2.get(`/api/incentive/scheme/${schemeId}/parameter`),
  getArLadders: (schemeId) => modelBaseV2.get(`/api/incentive/scheme/${schemeId}/ladder`),
  getCaps: (schemeId) => modelBaseV2.get(`/api/incentive/scheme/${schemeId}/cap`),
  getGrades: (schemeId) => modelBaseV2.get(`/api/incentive/scheme/${schemeId}/grade`),

  getRateMatrix: (schemeId, componentCode) => {
    return modelBaseV2.get(`/api/incentive/scheme/${schemeId}/matrix/${componentCode}`);
  },

  /**
   * Who last changed each configuration row, newest first.
   *
   * NOT a change history: FFIS has no history table, only create/update stamps
   * per row, so a rate edited three times appears once showing the last edit.
   */
  getAudit: (schemeId) => modelBaseV2.get(`/api/incentive/scheme/${schemeId}/audit`),

  /* ---- writes ----------------------------------------------------------- */

  saveComponent: (schemeId, body) => {
    return modelBaseV2.post(`/api/incentive/scheme/${schemeId}/component`, { body });
  },

  /**
   * Replaces the scheme's whole parameter set.
   *
   * Send `active` back as it was read. Nine seeded parameters are deliberately
   * inactive pending a business answer - SC+ bonus threshold and amount, the EC
   * platform multiplier, the store-size eligibility floor, the SEIN AV ASP band
   * rule, the TSE score-to-pay curve, the SAVINA outstation scaling - and each
   * would change somebody's pay if switched on.
   */
  saveParameters: (schemeId, rows) => {
    return modelBaseV2.post(`/api/incentive/scheme/${schemeId}/parameter`, { body: { rows } });
  },

  /**
   * Replaces the whole matrix for this component and matrixCode. A partial post
   * DELETES the cells it omits, so always send the full matrix.
   */
  saveRateMatrix: (schemeId, componentCode, matrixCode, rows) => {
    return modelBaseV2.post(
      `/api/incentive/scheme/${schemeId}/matrix/${componentCode}`,
      { body: { matrixCode, rows } });
  },

  /** Replaces the whole ladder for this component and ladderCode. */
  saveArLadder: (schemeId, componentCode, ladderCode, rows) => {
    return modelBaseV2.post(
      `/api/incentive/scheme/${schemeId}/ladder`,
      { body: { componentCode, ladderCode, rows } });
  },

  saveCaps: (schemeId, rows) => {
    return modelBaseV2.post(`/api/incentive/scheme/${schemeId}/cap`, { body: { rows } });
  },

  saveGrades: (schemeId, rows) => {
    return modelBaseV2.post(`/api/incentive/scheme/${schemeId}/grade`, { body: { rows } });
  },

  /**
   * Bulk per-SKU amounts. replaceExisting defaults to true on the server, which
   * clears the scheme's existing SKU amounts first.
   *
   * There is no GET counterpart - no stored procedure reads SKU amounts back, and
   * IncentiveSchemeDetailGet does not include them - so this is write-only until
   * a read route exists.
   */
  uploadSkuAmounts: (schemeId, body) => {
    return modelBaseV2.post(`/api/incentive/scheme/${schemeId}/sku/upload`, { body });
  },
};

export default IncentiveSchemeVariableModel;
